/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { IS_REGION_NZ } from '@gh/common-shared';
import { EntityVersion, ID, WithDirtyType, WithVersion } from '@gh/common-shared-data';
import { createEnum, EnumSet, WithOptimisticLock } from '@gh/core-data';

import { CommunicationListItemDto } from '../communications';
import {
  ContactCustomerListItemDto,
  ContactCustomerType,
  ContactDetailsShortDto,
  ContactType,
} from '../contacts';
import { SalesFolderShortDto, SalesFolderStatus } from './sales-folder.dto';

export const Priority = createEnum('PRIORITY', {
  Other: 'OTHER',
  A: 'A',
  B: 'B',
  C: 'C',
  D1: 'D1',
  Nurture: 'NURTURE',
});
export type Priority = EnumSet<typeof Priority>;

export const Stage = createEnum('STAGE', {
  New: 'NEW',
  Introduction: 'INTRODUCTION',
  Discovery: 'DISCOVERY',
  Qualified: 'QUALIFIED',
  SiteDiscussion: 'SITE_DISCUSSION',
  ConceptDiscussion: 'CONCEPT_DISCUSSION',
  Proposal: 'PROPOSAL',
  /** @deprecated Display name is Closed Won (AU/US code 6). Kept so existing Stage.Closed references compile. */
  Closed: 'CLOSED',
  ClosedWon: 'CLOSED_WON',
  ClosedLost: 'CLOSED_LOST',
});
export type Stage = EnumSet<typeof Stage>;

/**
 * Kanban column order for STAGE code_item values after GJGSAM-2517.
 * 6 = AU/US Closed Won and NZ ATP Signed; 8 = NZ Closed Won; 7 = Closed Lost.
 */
export const STAGE_BOARD_ORDER = [0, 1, 2, 3, 4, 5, 6, 8, 7];
export const CLOSED_LOST_STAGE_CODE = 7;
/** The stage Complete Sale moves a lead to: 6 in AU/US, 8 in NZ (where 6 is ATP Signed). Mirrors ManualStageChangePolicy. */
export const CLOSED_WON_STAGE_CODE = IS_REGION_NZ ? 8 : 6;

export function compareStageItemCodes(left: string | number, right: string | number): number {
  const leftCode = Number(left);
  const rightCode = Number(right);
  const leftRank = STAGE_BOARD_ORDER.indexOf(leftCode);
  const rightRank = STAGE_BOARD_ORDER.indexOf(rightCode);
  const leftKey = leftRank === -1 ? 1000 + leftCode : leftRank;
  const rightKey = rightRank === -1 ? 1000 + rightCode : rightRank;
  return leftKey - rightKey;
}

/** Lead list date filter type (Corporate/Master leads). */
export const LeadsDatesFilter = createEnum('LEADS_DATES_FILTERS', {
  CreateDate: 1,
  LastContactDate: 2,
});
export type LeadsDatesFilter = EnumSet<typeof LeadsDatesFilter>;

export type LeadDetailsDto = WithOptimisticLock<{
  id: ID;
  name: string;
  customer: ContactCustomerListItemDto;
  address: string;
  salesPerson: ContactDetailsShortDto;
  customerType: ContactCustomerType;
}>;

export type LeadDetailsInitDto = WithVersion<{
  contactId: ID;
  contactName: string;
  name: string;
  address: string;
  details: string;
  referredBy: any;
  salespersonId: ID;
  salespersonName: string;
  salesPerson: ContactDetailsShortDto;
  typeOfCustomer: ContactCustomerType;
  communications: CommunicationListItemDto[];
  salesFolders: SalesFolderShortDto[];
  promptPrint: boolean;
  isDppJobAllowed: boolean;
  isUnsubscribed: boolean;
  gDriveUrl: string;
  gDriveFolderId: ID;
  email: string;
  phone?: string;
  communityId?: ID;
  communityName?: string;
  startDate?: Date;
  activeSalesFolderId?: ID;
}>;

export type LeadSalesFolderSaveDto = WithDirtyType<{
  salesFolderId: ID;
  name: string;
  priority: Priority;
  status: SalesFolderStatus;
  notProceedingReason: number | string;
  notProceedingReasonOther?: string;
  reEngageDate?: Date;
  stage: Stage;
  followUp: Date;
  version: EntityVersion;
  createdLocal: Date;
}>;

export type LeadDetailsSaveDto = WithDirtyType<WithVersion<{
  contactId: ID;
  salespersonId: ID;
  typeOfCustomer: ContactCustomerType;
  salesFolder?: LeadSalesFolderSaveDto;
}>>;

export type LeadDetailsSaveResponseDto = LeadDetailsInitDto;

export type LeadCustomersListFilterDto = {
  type: ContactType[];
  name?: string;
  autocomplete?: boolean;
};

export type LeadListFilter = {
  autocomplete?: boolean;
  searchString?: string;
  showOverdue?: boolean;
  showCompleted?: boolean;
  showInactive?: boolean;
  showActive?: boolean;
  showSuppressed?: boolean;
  /** Visible list columns for dynamic SELECT. Omit for full projection. */
  fields?: string[];
};

export type LeadListItemDto = WithOptimisticLock<{
  contactId: ID;
  leadId?: number;
  code: string;
  address: string;
  city?: string;
  state?: string;
  postcode?: string;
  communityName: string;
  lastContactDate: Date;
  referredBy: number;
  contactPhone: string;
  contactEmail: string;
  activeSalesFolderId: ID;
  meetingSfPriorityId: number;
  meetingSfStageId: number;
  meetingSfStatusId: number;
  createdDate: Date;
  officeId?: ID;
  officeName?: string;
  salesperson: string;
  salespersonId: number;
  masterZoneName?: string;
  smsUnSubscribed: boolean;
  nextActivity?: Date;
  customerGoogleFolderId?: string;
  notProceedingReason?: number;
  isSuppressed: boolean;
  isImported: boolean;
}>;

export type BulkEditLeadsDto = {
  contactIds: ID[];
  status?: number;
  notProceedingReasonId?: number;
  notProceedingReasonOther?: string;
  stageId?: number;
  priorityId?: number;
};

export type BulkReassignLeadsDto = {
  leadIds: ID[];
  targetOfficeId: ID;
  targetSalespersonIds: ID[];
  useRoundRobin: boolean;
};

export type CopyArchiveLeadDto = {
  leadId: ID;
  targetOfficeId: ID;
  targetSalespersonId: ID;
};

export type SalesManagementReportHistoryItemDto = {
  forMonth: number;
  forYear: number;
  isFromHistory: boolean;
};

export type SalesManagementReportFilterDto = {
  forMonth: number;
  forYear: number;
  showInactiveSalespersons: boolean;
  calculateNewFigures: boolean;
  recalculateFigures: boolean;
  useExistingFigures: boolean;
  showLeadsContacts: boolean;
  months: SalesManagementReportHistoryItemDto[];
};

export const SalesManagementCalculationType = createEnum('SALES_MANAGEMENT_CALCULATION_TYPE', {
  CalculateNewFigures: 1,
  RecalculateFigures: 2,
  UseExistingFigures: 3,
});
export type SalesManagementCalculationType = EnumSet<typeof SalesManagementCalculationType>;

export type LeadsListFilterDto = {
  autocomplete?: boolean;
  searchString?: string;
  showInactive?: boolean;
  showActive?: boolean;
  showOverdue?: boolean;
  showCompleted?: boolean;
  salesPersonId?: string | string[];
  stage?: string;
  priority?: string;
  referredBy?: string;
  masterZoneId?: string;
  officeId?: string | string[];
  fromDate?: Date;
  toDate?: Date;
  dateType?: LeadsDatesFilter;
  showSuppressed?: boolean;
  /** Visible list columns for dynamic SELECT. Omit for full projection. */
  fields?: string[];
};
